import React, { Suspense } from 'react';
import { CA_REPORT, CostComparison, CostPerUnit, CreateLineItemDTO, ReportLineItem, ReportLineItemTypes } from './report.type';
import { Division, LineItemForPrediction, LineItemLabelEnum } from '../glossary/glossary.type';
import { Accordion, AccordionDetails, AccordionSummary, Alert, CircularProgress, Divider, Grid, Stack } from '@mui/material';
import ReportDetailsCard from './ReportDetailsCard';
import GeneralInfoCard from './GeneralInfoCard';
import Card from '../general/Card';
import { AttachMoney, CompareArrows, ExpandMore, Recommend } from '@mui/icons-material';
import { REPORT_COLORS } from './constants';
import { formatCurrency, formatPercentage } from '../../common/utils/common.util';
import { handleError } from '../../common/utils/util.errors';
import { toast } from 'react-toastify';
import { usePrompt } from '../../components/AppPrompt';
import DynamicDataCard from './DynamicDataCard';
import CAComparableDetails from './CAComparableDetails';
import { NotesAccordion } from '../general/Notes/NotesAccordion';
import { Box, Typography } from 'components/index';
import ReportDivisionTable from './ReportDivisionTable';
import ReportFooter from './ReportFooter';
import { ReturnCompPricingFunction } from './Report';
import { type MRT_ColumnDef } from 'material-react-table';
import {
  TOOLTIPS,
  generateHigherDivisionsNote,
  generateLowerDivisionsNote,
  generateOHPNote,
  generateContingencyNote,
  generateLowContingencyNote,
  generateBuyoutNote,
} from './report.util';
import { NoteDTO } from '../../common/utils/util.type';
import { deleteNoteFromReport, getNotes, addOrUpdateNoteToReport, addOrUpdateNotesToReport } from './report.api';
const ReportDivisionTables = React.lazy(() => import('./ReportDivisionTables'));

interface Props {
  divisionTables: {
    [key: string]: any[];
  };
  sortedDivisions: Division[];
  sortedTables: any;
  report: CA_REPORT;
  columns: MRT_ColumnDef<any>[];
  isLocked: boolean;
  isReportDirty: boolean;
  predictingDivisions: boolean;
  softCostSubTotalRow: any[];
  hardCostSubTotalRow: any[];
  projectedHardCostSubTotalRow: any[];
  totalProjectCostsRow: any[];
  costPerUnit: CostPerUnit;
  costComparison: CostComparison;
  isReportLoading: boolean;
  compLoading: boolean;
  isDeletingComp: boolean;
  deletingCompId: any | null;
  totalBudget: number;
  notes: NoteDTO[];
  isReportAuthorSameAsLoggedInUser: boolean;
  isDeletingReport: boolean;
  isAddingComps: boolean;
  isSaving: boolean;
  toggleLock: () => Promise<void>;
  onDeleteReport: () => Promise<void>;
  onDeleteComp: (compId: any) => Promise<void>;
  handleSavePdf: () => Promise<void>;
  handleCreateCopy: () => void;
  handleGlobalAddComps: () => Promise<void>;
  saveReportWithCalculations: () => void;
  handleOpenCompsDialog: () => void;
  setNotes: React.Dispatch<React.SetStateAction<NoteDTO[]>>;
  saveLineItem: (lineItemData: CreateLineItemDTO, showNotification?: boolean) => Promise<void>;
  openAutoAssignDialog: (lineItemsToAutoAssign: LineItemForPrediction[]) => Promise<void>;
  updateReport: (updateFn: (prevReport: CA_REPORT) => CA_REPORT) => void;
  updateLineItem: (updatedLineItem: ReportLineItem) => void;
  setSelectedLineItem: (lineItem: ReportLineItem | null) => void;
  setLineItemEditDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setLineItemNoteDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenProjectDialog: (divisionId: string, divisionCode: string, divisionName: string) => void;
  registerReturnComp: (divisionId: string, returnCompFunc: ReturnCompPricingFunction | undefined) => void;
  updateGeneralInfo: (generalInfo: any) => void;
  updateDynamicData: (updatedString: string) => void;
  updateReportWithModifiedLineItems: (modifiedLineItems: ReportLineItem[]) => void;
  totalUnassignedCost: number;
  selectedComparableIds: string[];
  setSelectedComparableIds: React.Dispatch<React.SetStateAction<string[]>>;
  onSelectDivisionsForComps: () => void;
  onExportInvoice?: () => void;
}

const ReportRenderer: React.FC<Props> = ({
  divisionTables,
  sortedDivisions,
  sortedTables,
  report,
  columns,
  isLocked,
  predictingDivisions,
  softCostSubTotalRow,
  hardCostSubTotalRow,
  projectedHardCostSubTotalRow,
  totalProjectCostsRow,
  costPerUnit,
  costComparison,
  totalBudget,
  isReportLoading,
  isReportAuthorSameAsLoggedInUser,
  isDeletingReport,
  isAddingComps,
  isSaving,
  compLoading,
  isDeletingComp,
  deletingCompId,
  notes,
  setNotes,
  onDeleteReport,
  onDeleteComp,
  handleOpenCompsDialog,
  openAutoAssignDialog,
  handleSavePdf,
  handleCreateCopy,
  handleGlobalAddComps,
  saveReportWithCalculations,
  toggleLock,
  updateReport,
  isReportDirty,
  updateLineItem,
  setSelectedLineItem,
  saveLineItem,
  setLineItemEditDialogOpen,
  setLineItemNoteDialogOpen,
  handleOpenProjectDialog,
  registerReturnComp,
  updateGeneralInfo,
  updateDynamicData,
  updateReportWithModifiedLineItems,
  totalUnassignedCost,
  selectedComparableIds,
  setSelectedComparableIds,
  onSelectDivisionsForComps,
  onExportInvoice,
}) => {
  const getDivisionTotal = (divisionCode: string): number => {
    const divisionIndex = sortedDivisions.findIndex(d => d.code === divisionCode);
    if (divisionIndex === -1) return 0;
    const lineItems: ReportLineItem[] = sortedTables[divisionIndex];
    if (!lineItems) return 0;
    const normalLineItems =
      lineItems.filter(
        row => row.type == null || row.type === ReportLineItemTypes.NORMAL || row.type === ReportLineItemTypes.CompPricing,
      ) || [];
    return normalLineItems.reduce((acc, item) => acc + item.totalBudget, 0);
  };

  const division1BudgetTotal = getDivisionTotal('1.00');
  const division17BudgetTotal = getDivisionTotal('17.00');

  const totalProjectedCost = totalProjectCostsRow[0]?.totalBudget;
  let overheadAndProfitPercentage: number | null = null;
  if (totalProjectedCost && totalProjectedCost > 0) {
    overheadAndProfitPercentage = ((division1BudgetTotal + division17BudgetTotal) / totalProjectedCost) * 100;
  }

  const [isGeneratingNotes, setIsGeneratingNotes] = React.useState(false);
  const { getConfirmation } = usePrompt();

  const handleAutogenerateNotes = async () => {
    try {
      const isConfirmed = await getConfirmation({
        description: 'This will replace all previously auto-generated notes. Are you sure you want to continue?',
        confirmButton: {
          label: 'Continue',
          color: 'error',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });
      if (!isConfirmed) return;

      setIsGeneratingNotes(true);
      const noteParams = {
        report,
        sortedDivisions,
        divisionTables,
        projectedHardCostSubTotalRow,
        totalProjectCostsRow,
        overheadAndProfitPercentage,
      };

      const notes = [];

      // Generate higher divisions note
      const higherNote = generateHigherDivisionsNote(noteParams);
      if (higherNote) {
        notes.push({
          content: higherNote,
          isAutoGenerated: true,
        });
      }

      // Generate lower divisions note
      const lowerNote = generateLowerDivisionsNote(noteParams);
      if (lowerNote) {
        notes.push({
          content: lowerNote,
          isAutoGenerated: true,
        });
      }

      // Generate OH&P note
      const ohpNote = generateOHPNote(noteParams);
      if (ohpNote) {
        notes.push({
          content: ohpNote,
          isAutoGenerated: true,
        });
      }

      // Generate contingency notes
      const noContingencyNote = generateContingencyNote(noteParams);
      const lowContingencyNote = generateLowContingencyNote(noteParams);

      // Only add one of the contingency notes - either no contingency or low contingency
      if (noContingencyNote) {
        notes.push({
          content: noContingencyNote,
          isAutoGenerated: true,
        });
      } else if (lowContingencyNote) {
        notes.push({
          content: lowContingencyNote,
          isAutoGenerated: true,
        });
      }

      // Always add the buyout note at the end
      const buyoutNote = generateBuyoutNote();
      notes.push({
        content: buyoutNote,
        isAutoGenerated: true,
      });

      await addOrUpdateNotesToReport({
        reportId: report.id,
        notes,
        isAutoGenerated: true,
        deletePreviousAutoGeneratedNotes: true,
      });

      // Refresh notes list
      const updatedNotes = await getNotes(report.id);
      setNotes(updatedNotes);
      toast.success('Notes autogenerated successfully');
    } catch (error) {
      handleError(error);
    } finally {
      setIsGeneratingNotes(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 90px)' }}>
      {totalUnassignedCost > 0 && (
        <Box sx={{ position: 'sticky', top: '80px', zIndex: 1000, backgroundColor: 'white' }}>
          <Alert severity='warning' sx={{ my: 2, mx: 'auto' }}>
            There are unassigned line items in this cost analysis! The total unassigned cost is ${totalUnassignedCost.toLocaleString()}
          </Alert>
        </Box>
      )}

      {report.errorMessage ? <Alert severity='error'>{report.errorMessage}</Alert> : null}
      <Grid container spacing={2} py={2}>
        <Grid size={{ xs: 12 }}>
          <ReportDetailsCard report={report} updateReport={updateReport} isLocked={isLocked} cardProps={{ itemsPerRow: 3 }} />
        </Grid>
        <Grid size={{ xs: 6, lg: 3 }}>
          <GeneralInfoCard
            generalInfo={report.generalInfo}
            updateGeneralInfo={updateGeneralInfo}
            isLocked={isLocked}
            reportStatus={report.status}
          />
        </Grid>
        <Grid size={{ xs: 6, lg: 3 }}>
          <Card
            icon={<AttachMoney color='primary' />}
            heading='Budgeted Cost Per Unit'
            backgroundColor={REPORT_COLORS.BLUE}
            data={{
              'Hard Cost / Building GSF':
                costPerUnit.hardCostPerBuildingGSF != 0 ? formatCurrency(costPerUnit.hardCostPerBuildingGSF.toString()) : '--',
              'Project Cost / Building NRSF':
                costPerUnit.projectCostPerBuildingNRSF != 0 ? formatCurrency(costPerUnit.projectCostPerBuildingNRSF.toString()) : '--',
              'Project Cost / Unit': costPerUnit.projectCostPerUnit != 0 ? formatCurrency(costPerUnit.projectCostPerUnit.toString()) : '--',
            }}
            itemsPerRow={1}
          />
        </Grid>
        <Grid size={{ xs: 6, lg: 3 }}>
          <Card
            icon={<CompareArrows color='primary' />}
            heading='Hard Cost Comparison'
            backgroundColor={REPORT_COLORS.BLUE}
            data={{
              'Contractor Hard Cost Total': formatCurrency(costComparison.contractorHardCostTotal.toString()),
              'CDCC Hard Cost Total': formatCurrency(costComparison.cdccHardCostTotal.toString()),
              'CDCC Cost / GSF': formatCurrency(costComparison.cdccCostPerGSF.toString()),
              '% Deviation':
                costComparison.deviationPercentage == 0 ? '--' : formatPercentage((costComparison.deviationPercentage * 100).toString()),
            }}
            itemsPerRow={1}
          />
        </Grid>
        <Grid size={{ xs: 6, lg: 3 }}>
          <DynamicDataCard
            dynamicDataString={report.dynamicData}
            updateDynamicData={updateDynamicData}
            isLocked={isLocked}
            reportStatus={report.status}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <CAComparableDetails
            report={report}
            compLoading={isReportLoading || compLoading}
            isLocked={isLocked}
            isDeletingComp={isDeletingComp}
            deletingCompId={deletingCompId}
            onDeleteComp={onDeleteComp}
            handleOpenCompsDialog={handleOpenCompsDialog}
            selectedComparableIds={selectedComparableIds}
            setSelectedComparableIds={setSelectedComparableIds}
            onSelectDivisionsForComps={onSelectDivisionsForComps}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2, borderColor: 'GrayText', borderWidth: 0.8 }} />
      <Grid container spacing={2}>
        <Suspense
          fallback={
            <Stack flex={1} alignItems={'center'} m={'auto'}>
              <CircularProgress />
              <Typography variant='h6'>Loading Divisions...</Typography>
            </Stack>
          }
        >
          <ReportDivisionTables
            divisionTables={divisionTables}
            sortedDivisions={sortedDivisions}
            sortedTables={sortedTables}
            report={report}
            columns={columns}
            isLocked={isLocked}
            saveLineItem={saveLineItem}
            predictingDivisions={predictingDivisions}
            softCostSubTotalRow={softCostSubTotalRow}
            hardCostSubTotalRow={hardCostSubTotalRow}
            projectedHardCostSubTotalRow={projectedHardCostSubTotalRow}
            totalProjectCostsRow={totalProjectCostsRow}
            totalUnassignedCost={totalUnassignedCost}
            openAutoAssignDialog={openAutoAssignDialog}
            updateLineItem={updateLineItem}
            setSelectedLineItem={setSelectedLineItem}
            setLineItemEditDialogOpen={setLineItemEditDialogOpen}
            setLineItemNoteDialogOpen={setLineItemNoteDialogOpen}
            handleOpenProjectDialog={handleOpenProjectDialog}
            registerReturnComp={registerReturnComp}
            updateReportWithModifiedLineItems={updateReportWithModifiedLineItems}
            overheadAndProfitPercentage={overheadAndProfitPercentage}
            costThreshold={report.generalInfo?.costThreshold}
          />
        </Suspense>
      </Grid>
      <Grid size={{ xs: 12 }} key='grandTotals' sx={{ mt: 2 }}>
        <Card
          icon={<AttachMoney color='primary' />}
          heading='Grand Totals'
          data={{
            'Revised Budget': formatCurrency(totalBudget.toString()),
          }}
        />
      </Grid>
      <Grid size={{ xs: 12 }} key='notes' sx={{ mt: 2 }}>
        <NotesAccordion
          entityId={report.id}
          notes={notes}
          setNotes={setNotes}
          isLocked={isLocked}
          reportStatus={report.status}
          apiCalls={{
            getNotes: getNotes,
            addOrUpdateNote: addOrUpdateNoteToReport,
            deleteNote: deleteNoteFromReport,
          }}
          tooltips={{ unlockToEdit: TOOLTIPS.unlockToEdit, reportStatus: TOOLTIPS.statusCompleted }}
          onAutogenerateNotes={handleAutogenerateNotes}
          isAutoGenerating={isGeneratingNotes}
        />
      </Grid>
      <Grid size={{ xs: 12 }} key='allowances' sx={{ mt: 2 }}>
        <Accordion elevation={2} sx={{ borderRadius: '10px !important' }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display='flex' alignItems='center'>
              <Recommend color='primary' />
              <Typography variant='h6' color='primary' sx={{ ml: 1, mb: 0 }}>
                ALLOWANCES
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <ReportDivisionTable
              key={'allowances'}
              division={null}
              reportId={report.id}
              reportStatus={report.status}
              reportSelectedComps={report.selectedComps}
              gridData={report.lineItems.filter(li => li.label === LineItemLabelEnum.Allowances)}
              divisions={sortedDivisions}
              updateLineItem={updateLineItem}
              globalSelectedLineItemsRef={{ current: [] } as React.MutableRefObject<ReportLineItem[]>}
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              onUpdateGlobalSelection={() => {}}
              setSelectedLineItem={setSelectedLineItem}
              setLineItemEditDialogOpen={setLineItemEditDialogOpen}
              setLineItemNoteDialogOpen={setLineItemNoteDialogOpen}
              handleOpenProjectDialog={handleOpenProjectDialog}
              saveLineItem={saveLineItem}
              reportGeneralInfo={{ ...report.generalInfo, elevatorCount: report.project?.elevatorCount }}
              totalProjectCost={totalProjectCostsRow[0]?.totalBudget || 0}
              isLocked={true}
              name='ALLOWANCES'
              showActions={false}
              hideNotes={true}
              updateReportWithModifiedLineItems={updateReportWithModifiedLineItems}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>
      <ReportFooter
        report={report}
        isReportDirty={isReportDirty}
        isLocked={isLocked}
        isReportAuthorSameAsLoggedInUser={isReportAuthorSameAsLoggedInUser}
        isDeletingReport={isDeletingReport}
        isAddingComps={isAddingComps}
        isReportLoading={isReportLoading}
        toggleLock={toggleLock}
        onDeleteReport={onDeleteReport}
        handleOpenCompsDialog={handleOpenCompsDialog}
        handleSavePdf={handleSavePdf}
        handleCreateCopy={handleCreateCopy}
        handleGlobalAddComps={handleGlobalAddComps}
        saveReport={saveReportWithCalculations}
        isSaving={isSaving}
        onExportInvoice={onExportInvoice}
      />
    </div>
  );
};

export default React.memo(ReportRenderer);
